import { CustomerInfo, PurchasesOfferings, PurchasesPackage } from '@revenuecat/purchases-capacitor';

/**
 * Mutations for the Subscription store module
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Store/Module/Subscription
 * @project ChalkySticks
 */
const mutations = {
	/**
	 * Clear all subscription data
	 *
	 * @param IRevenueCatState state
	 * @return void
	 */
	clearAll(state: IRevenueCatState): void {
		state.activePackage = null;
		state.availableOfferings = null;
		state.customerInfo = null;
		state.hasPremium = false;
		state.lastError = null;
		state.lastStatusCheck = null;
		state.selectedPackage = null;
	},

	/**
	 * Set active package/subscription
	 *
	 * @param IRevenueCatState state
	 * @param PurchasesPackage | null activePackage
	 * @return void
	 */
	setActivePackage(state: IRevenueCatState, activePackage: PurchasesPackage | null): void {
		state.activePackage = activePackage;
	},

	/**
	 * Set available offerings
	 *
	 * @param IRevenueCatState state
	 * @param PurchasesOfferings | null offerings
	 * @return void
	 */
	setAvailableOfferings(state: IRevenueCatState, offerings: PurchasesOfferings | null): void {
		state.availableOfferings = offerings;
	},

	/**
	 * Set customer information
	 *
	 * @param IRevenueCatState state
	 * @param CustomerInfo | null customerInfo
	 * @return void
	 */
	setCustomerInfo(state: IRevenueCatState, customerInfo: CustomerInfo | null): void {
		state.customerInfo = customerInfo;

		// Update premium status based on entitlements
		if (customerInfo) {
			state.hasPremium = Object.keys(customerInfo.entitlements.active).length > 0;
		} else {
			state.hasPremium = false;
		}
	},

	/**
	 * Set error message
	 *
	 * @param IRevenueCatState state
	 * @param string | null error
	 * @return void
	 */
	setError(state: IRevenueCatState, error: string | null): void {
		state.lastError = error;
	},

	/**
	 * Set premium status
	 *
	 * @param IRevenueCatState state
	 * @param boolean hasPremium
	 * @return void
	 */
	setHasPremium(state: IRevenueCatState, hasPremium: boolean): void {
		state.hasPremium = hasPremium;
	},

	/**
	 * Set loading offerings state
	 *
	 * @param IRevenueCatState state
	 * @param boolean isLoading
	 * @return void
	 */
	setIsLoadingOfferings(state: IRevenueCatState, isLoading: boolean): void {
		state.isLoadingOfferings = isLoading;
	},

	/**
	 * Set purchasing state
	 *
	 * @param IRevenueCatState state
	 * @param boolean isPurchasing
	 * @return void
	 */
	setIsPurchasing(state: IRevenueCatState, isPurchasing: boolean): void {
		state.isPurchasing = isPurchasing;
	},

	/**
	 * Set restoring state
	 *
	 * @param IRevenueCatState state
	 * @param boolean isRestoring
	 * @return void
	 */
	setIsRestoring(state: IRevenueCatState, isRestoring: boolean): void {
		state.isRestoring = isRestoring;
	},

	/**
	 * Set last status check timestamp
	 *
	 * @param IRevenueCatState state
	 * @param number | null timestamp
	 * @return void
	 */
	setLastStatusCheck(state: IRevenueCatState, timestamp: number | null): void {
		state.lastStatusCheck = timestamp;
	},

	/**
	 * Set selected package for purchase
	 *
	 * @param IRevenueCatState state
	 * @param PurchasesPackage | null selectedPackage
	 * @return void
	 */
	setSelectedPackage(state: IRevenueCatState, selectedPackage: PurchasesPackage | null): void {
		state.selectedPackage = selectedPackage;
	},
};

export default mutations;
